import { useRef, useState } from "react";
import { Play, Volume2, VolumeX, Maximize } from "lucide-react";
import { VIDEO_PLAYER } from "@/constants/testIds";

const ratioToCss = (r) => (r === "16:9" ? "16/9" : r === "9:16" ? "9/16" : "1/1");

export const VideoPlayer = ({ project }) => {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [error, setError] = useState(false);

  if (!project.videoUrl || error) {
    return (
      <div
        data-testid={VIDEO_PLAYER.unavailable}
        className="relative w-full mx-auto bg-black flex items-center justify-center"
        style={{ aspectRatio: ratioToCss(project.aspectRatio), maxHeight: "78vh" }}
      >
        <img src={project.posterUrl} alt={project.title} className="absolute inset-0 w-full h-full object-contain opacity-30" />
        <div className="relative z-10 text-center px-6">
          <p className="font-mono text-xs sm:text-sm uppercase tracking-widest text-[#888880]">Video unavailable</p>
          <p className="font-mono text-[10px] sm:text-xs uppercase tracking-widest text-[#555550] mt-2">
            {error ? "This sample clip failed to load" : "No sample clip provided for this preview"}
          </p>
        </div>
      </div>
    );
  }

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };
  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };
  const toggleFullscreen = () => {
    const el = wrapRef.current;
    if (!el) return;
    if (document.fullscreenElement) document.exitFullscreen();
    else el.requestFullscreen?.();
  };

  return (
    <div
      ref={wrapRef}
      data-testid={VIDEO_PLAYER.container}
      className="relative w-full mx-auto bg-black"
      style={{ aspectRatio: ratioToCss(project.aspectRatio), maxHeight: "78vh" }}
    >
      <video
        ref={videoRef}
        data-testid={VIDEO_PLAYER.nativeVideo}
        src={project.videoUrl}
        poster={project.posterUrl}
        controls={playing}
        playsInline
        muted={muted}
        preload="none"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onError={() => setError(true)}
        onClick={togglePlay}
        className="absolute inset-0 w-full h-full object-contain bg-black"
      />
      {!playing && (
        <button
          type="button"
          aria-label="Play video"
          data-testid={VIDEO_PLAYER.playButton}
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors group"
        >
          <span className="flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#CCFF00] text-black group-active:scale-95 transition-transform">
            <Play size={28} fill="black" />
          </span>
        </button>
      )}
      {playing && (
        <div className="absolute top-3 right-3 flex items-center gap-2 z-10">
          <button
            type="button"
            aria-label={muted ? "Unmute" : "Mute"}
            data-testid={VIDEO_PLAYER.muteButton}
            onClick={toggleMute}
            className="w-11 h-11 flex items-center justify-center bg-black/60 border border-[#2A2A2A] text-[#F5F5F0] hover:border-[#CCFF00] hover:text-[#CCFF00] transition-colors"
          >
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
          <button
            type="button"
            aria-label="Fullscreen"
            data-testid={VIDEO_PLAYER.fullscreenButton}
            onClick={toggleFullscreen}
            className="w-11 h-11 flex items-center justify-center bg-black/60 border border-[#2A2A2A] text-[#F5F5F0] hover:border-[#CCFF00] hover:text-[#CCFF00] transition-colors"
          >
            <Maximize size={18} />
          </button>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
